import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { PanelLateral } from '@/ui/PanelLateral'
import { ConfirmacionDestructiva } from '@/ui/ConfirmacionDestructiva'
import {
  PrevisualizacionImportacion,
  type FilaImportacion,
} from '@/ui/PrevisualizacionImportacion'
import { ENCF, Monto, RNC } from '@/ui/Numero'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { LineaDeTiempo } from '@/ui/LineaDeTiempo'
import { formatearRNC } from '@/lib/rnc'
import { Seccion, Tarjeta } from './-componentes'

export const Route = createFileRoute('/sistema/superposiciones')({
  component: CatalogoSuperposiciones,
})

const FILAS_IMPORTACION: readonly FilaImportacion[] = [
  {
    numero: 1,
    valores: { rnc: <RNC valor="131880738" />, nombre: 'Ferretería Sánchez', tipo: 'Cliente' },
    resultado: 'ok',
  },
  {
    numero: 2,
    valores: { rnc: <RNC valor="130445120" />, nombre: 'Transporte Rivas', tipo: 'Cliente' },
    resultado: 'ok',
  },
  {
    numero: 3,
    valores: { rnc: <RNC valor="1318807" />, nombre: 'Colmado La Esquina', tipo: 'Cliente' },
    resultado: 'error',
    mensaje: 'RNC mal formado: 131-8807-38',
  },
  {
    numero: 4,
    valores: { rnc: <RNC valor="131880738" />, nombre: 'Ferretería Sanchez SRL', tipo: 'Cliente' },
    resultado: 'aviso',
    mensaje: 'duplica la fila 1',
  },
  {
    numero: 5,
    valores: { rnc: <RNC valor="130998812" />, nombre: 'Suplidora del Este', tipo: 'Proveedor' },
    resultado: 'ok',
  },
]

function CatalogoSuperposiciones() {
  const [panelAbierto, setPanelAbierto] = useState(false)
  const [panelAnchoAbierto, setPanelAnchoAbierto] = useState(false)
  const [confirmacionAbierta, setConfirmacionAbierta] = useState(false)

  return (
    <div className="flex flex-col gap-8">
      <Seccion
        titulo="Panel lateral"
        nota="Detalle sin perder el listado detrás. Es la alternativa al modal y a la página nueva: el alta de un cliente se hace aquí para que el listado quede visible. Atrapa el foco y lo devuelve al cerrarse; Esc cierra."
      >
        <Tarjeta className="flex flex-wrap items-center gap-2">
          <Boton variante="secundario" onClick={() => setPanelAbierto(true)}>
            Abrir alta de cliente
          </Boton>
          <Boton variante="secundario" onClick={() => setPanelAnchoAbierto(true)}>
            Abrir detalle de documento
          </Boton>
        </Tarjeta>
      </Seccion>

      <Seccion
        titulo="Confirmación destructiva"
        nota="Se escribe el nombre de lo que se borra. No es burocracia: es el freno para lo que no tiene vuelta atrás. El botón queda deshabilitado hasta que lo escrito coincide exactamente."
      >
        <Tarjeta>
          <Boton variante="destructivo" onClick={() => setConfirmacionAbierta(true)}>
            Anular rango 100,001–110,000
          </Boton>
        </Tarjeta>
      </Seccion>

      <Seccion
        titulo="Previsualización de importación"
        nota="El mismo componente en contrapartes, productos y migración de histórico. Los errores se muestran por línea, con el valor que los causa, antes de confirmar: un «la importación falló» sobre 1,284 filas es inservible."
      >
        <PrevisualizacionImportacion
          titulo="Migrar contrapartes"
          contexto="Paso 3 de 4 · 1,284 filas · mapeo de columnas aplicado"
          columnas={[
            { clave: 'rnc', etiqueta: 'RNC', numerico: true },
            { clave: 'nombre', etiqueta: 'Nombre' },
            { clave: 'tipo', etiqueta: 'Tipo' },
          ]}
          filas={FILAS_IMPORTACION}
          onDescargarErrores={() => undefined}
          onConfirmar={() => undefined}
          onCancelar={() => undefined}
        />
      </Seccion>

      {/* Superposiciones ------------------------------------------------ */}
      <PanelLateral
        abierto={panelAbierto}
        onCerrar={() => setPanelAbierto(false)}
        titulo="Nuevo cliente"
        subtitulo="El listado queda visible detrás"
        pie={
          <>
            <Boton variante="secundario" onClick={() => setPanelAbierto(false)}>
              Cancelar
            </Boton>
            <Boton variante="primario" onClick={() => setPanelAbierto(false)}>
              Guardar
            </Boton>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <CampoTexto
            etiqueta="RNC o cédula"
            obligatorio
            numerico
            defaultValue="1-31-88073-8"
            ayuda="Al escribirlo se autocompleta el nombre desde el directorio de DGII."
          />
          <CampoTexto etiqueta="Nombre comercial" obligatorio defaultValue="Ferretería Sánchez" />
          <CampoTexto etiqueta="Correo" defaultValue="compras@ferreteriasanchez.do" />
        </div>
      </PanelLateral>

      <PanelLateral
        abierto={panelAnchoAbierto}
        onCerrar={() => setPanelAnchoAbierto(false)}
        ancho="ancho"
        titulo="Factura de Crédito Fiscal"
        subtitulo={
          <span className="flex items-center gap-3">
            <ENCF valor="E310000000847" />
            <span>Ferretería Sánchez · {formatearRNC('131880738')}</span>
          </span>
        }
        pie={
          <>
            <Boton variante="secundario">Descargar PDF</Boton>
            <Boton variante="secundario">Descargar XML</Boton>
            <Boton variante="secundario">Emitir nota de crédito</Boton>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col gap-3">
            <table className="w-full border-collapse">
              <caption className="solo-lectores">Totales del documento</caption>
              <tbody>
                <tr className="h-[var(--alto-fila-normal)] border-b border-gris-200">
                  <td className="text-gris-600">Subtotal</td>
                  <td className="text-right">
                    <Monto valor={25200} />
                  </td>
                </tr>
                <tr className="h-[var(--alto-fila-normal)] border-b border-gris-200">
                  <td className="text-gris-600">ITBIS 18%</td>
                  <td className="text-right">
                    <Monto valor={4536} />
                  </td>
                </tr>
                <tr className="h-[var(--alto-fila-normal)]">
                  <td className="font-semibold text-gris-900">Total</td>
                  <td className="text-right font-semibold">
                    <Monto valor={29736} mostrarMoneda />
                  </td>
                </tr>
              </tbody>
            </table>
            <InsigniaEstado tono="verde">Aceptado</InsigniaEstado>
          </div>

          <LineaDeTiempo
            eventos={[
              { id: '1', etiqueta: 'Emitido', momento: '2026-09-21T18:32:07Z', detalle: 'por Ana M.', tono: 'marca' },
              { id: '2', etiqueta: 'Firmado', momento: '2026-09-21T18:32:08Z', tono: 'marca' },
              {
                id: '3',
                etiqueta: 'Enviado a DGII',
                momento: '2026-09-21T18:32:09Z',
                detalle: 'TrackId a7f3-9b2c',
                tono: 'marca',
              },
              { id: '4', etiqueta: 'Aceptado', momento: '2026-09-21T18:32:11Z', tono: 'verde' },
            ]}
          />
        </div>
      </PanelLateral>

      <ConfirmacionDestructiva
        abierto={confirmacionAbierta}
        onCancelar={() => setConfirmacionAbierta(false)}
        onConfirmar={() => setConfirmacionAbierta(false)}
        titulo="Anular el rango 100,001–110,000"
        descripcion={
          <>
            El rango deja de estar disponible para emitir y el hueco queda registrado con su causa.
            No se puede deshacer.
          </>
        }
        textoAEscribir="100001-110000"
        etiquetaConfirmar="Anular el rango"
      />
    </div>
  )
}
